import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useAuth } from '../hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';

const DashboardContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(180deg, #000000 0%, #0A1128 100%);
  color: white;
  display: flex;
`;

const Sidebar = styled.div`
  width: 250px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  padding: 2rem 0;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  display: flex;
  flex-direction: column;
`;

const SidebarLogo = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(to right, #ffffff, #4B63D3);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: 0.1rem;
  padding: 0 2rem 2rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const SidebarNav = styled.nav`
  margin-top: 2rem;
  flex: 1;
`;

const NavItem = styled.button<{ active?: boolean }>`
  width: 100%;
  padding: 1rem 2rem;
  background: ${props => props.active ? 'rgba(75, 99, 211, 0.1)' : 'transparent'};
  border: none;
  color: ${props => props.active ? '#ffffff' : 'rgba(255, 255, 255, 0.7)'};
  text-align: left;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border-left: 3px solid ${props => props.active ? '#4B63D3' : 'transparent'};

  &:hover {
    background: rgba(75, 99, 211, 0.1);
    color: white;
  }
`;

const LogoutButton = styled(NavItem)`
  color: #ff4444;
  margin-top: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  &:hover {
    background: rgba(255, 68, 68, 0.1);
    color: #ff4444;
  }
`;

const MainSection = styled.div`
  margin-left: 250px;
  width: calc(100% - 250px);
  min-height: 100vh;
  padding: 2rem;
`;

const SettingsContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  color: white;
  font-size: 2rem;
  margin-bottom: 0.5rem;
`;

const PageDescription = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 1rem;
`;

const Section = styled.section`
  background: rgba(255, 255, 255, 0.03);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
`;

const SectionHeader = styled.div`
  margin-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  color: #4B63D3;
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
`;

const SectionDescription = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #4B63D3;
    background: rgba(255, 255, 255, 0.08);
    box-shadow: 0 0 0 3px rgba(75, 99, 211, 0.1);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const SaveButton = styled.button`
  background: #4B63D3;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;

  &:hover {
    background: #3b4fa8;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const UserInfo = styled.div`
  padding: 1rem 2rem;
  background: rgba(255, 255, 255, 0.05);
  margin: 0 1rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
`;

// Icons
const HomeIcon = () => <span>🏠</span>;
const SettingsIcon = () => <span>⚙️</span>;
const LogoutIcon = () => <span>🚪</span>;

const IntegrationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
`;

const IntegrationCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s;

  &:hover {
    border-color: rgba(75, 99, 211, 0.4);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  }
`;

const IntegrationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const IntegrationIcon = styled.div`
  width: 40px;
  height: 40px;
  background: rgba(75, 99, 211, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
`;

const IntegrationInfo = styled.div`
  flex: 1;
`;

const IntegrationTitle = styled.h3`
  color: white;
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
`;

const IntegrationStatus = styled.div<{ connected: boolean }>`
  font-size: 0.8rem;
  color: ${props => props.connected ? '#4ade80' : '#ff4444'};
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${props => props.connected ? '#4ade80' : '#ff4444'};
  }
`;

const IntegrationDescription = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  margin: 1rem 0;
  line-height: 1.5;
`;

const ApiKeyDisplay = styled.div`
  background: rgba(0, 0, 0, 0.2);
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-family: monospace;
  color: rgba(255, 255, 255, 0.7);
  letter-spacing: 0.1em;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  background: ${props => {
    switch (props.variant) {
      case 'primary':
        return '#4B63D3';
      case 'danger':
        return 'rgba(255, 68, 68, 0.1)';
      default:
        return 'rgba(255, 255, 255, 0.05)';
    }
  }};
  color: ${props => props.variant === 'danger' ? '#ff4444' : 'white'};
  border: 1px solid ${props => {
    switch (props.variant) {
      case 'primary':
        return '#4B63D3';
      case 'danger':
        return 'rgba(255, 68, 68, 0.2)';
      default:
        return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => {
      switch (props.variant) {
        case 'primary':
          return '#3b4fa8';
        case 'danger':
          return 'rgba(255, 68, 68, 0.2)';
        default:
          return 'rgba(255, 255, 255, 0.1)';
      }
    }};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

interface Integration {
  id: string;
  name: string;
  key: string;
  isEditing: boolean;
  icon: string;
  description: string;
  connected: boolean;
}

const Settings: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: user?.email || '',
    displayName: '',
    phoneNumber: '',
    company: '',
  });

  const [integrations, setIntegrations] = useState<Integration[]>([
    {
      id: 'clockify',
      name: 'Clockify',
      key: '',
      isEditing: false,
      icon: '⏱️',
      description: 'Track time spent on tasks and projects automatically. Integrate with Clockify for seamless time management.',
      connected: false
    },
    {
      id: 'slack',
      name: 'Slack',
      key: '',
      isEditing: false,
      icon: '💬',
      description: 'Connect with Slack to receive notifications and updates directly in your workspace channels.',
      connected: false
    },
    {
      id: 'openai',
      name: 'OpenAI',
      key: '',
      isEditing: false,
      icon: '🤖',
      description: "Leverage OpenAI powerful AI capabilities for enhanced automation and content generation.",
      connected: false
    }
  ]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    // TODO: Implement save functionality
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleIntegrationEdit = (id: string) => {
    setIntegrations(prev => prev.map(integration => ({
      ...integration,
      isEditing: integration.id === id ? true : integration.isEditing
    })));
  };

  const handleIntegrationSave = (id: string) => {
    setIntegrations(prev => prev.map(integration => ({
      ...integration,
      isEditing: integration.id === id ? false : integration.isEditing,
      connected: integration.id === id ? true : integration.connected
    })));
    // TODO: Implement actual integration save functionality
  };

  const handleIntegrationChange = (id: string, value: string) => {
    setIntegrations(prev => prev.map(integration => ({
      ...integration,
      key: integration.id === id ? value : integration.key
    })));
  };

  return (
    <DashboardContainer>
      <Sidebar>
        <SidebarLogo>COSMOS</SidebarLogo>
        <UserInfo>{user?.email}</UserInfo>
        <SidebarNav>
          <NavItem 
            active={location.pathname === '/dashboard'}
            onClick={() => handleNavigation('/dashboard')}
          >
            <HomeIcon /> Home
          </NavItem>
          <NavItem 
            active={location.pathname === '/dashboard/settings'}
            onClick={() => handleNavigation('/dashboard/settings')}
          >
            <SettingsIcon /> Settings
          </NavItem>
        </SidebarNav>
        <LogoutButton onClick={handleLogout}>
          <LogoutIcon /> Logout
        </LogoutButton>
      </Sidebar>

      <MainSection>
        <SettingsContainer>
          <PageHeader>
            <PageTitle>Settings</PageTitle>
            <PageDescription>Manage your account settings and preferences</PageDescription>
          </PageHeader>

          <form onSubmit={handleSubmit}>
            <Section>
              <SectionHeader>
                <SectionTitle>Personal Information</SectionTitle>
                <SectionDescription>Update your personal details and contact information</SectionDescription>
              </SectionHeader>

              <FormGroup>
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Enter your first name"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Enter your last name"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="displayName">Display Name</Label>
                <Input
                  id="displayName"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleInputChange}
                  placeholder="Choose how your name will appear"
                />
              </FormGroup>
            </Section>

            <Section>
              <SectionHeader>
                <SectionTitle>Contact Information</SectionTitle>
                <SectionDescription>Manage your contact details and communication preferences</SectionDescription>
              </SectionHeader>

              <FormGroup>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                  disabled
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="phoneNumber">Phone Number</Label>
                <Input
                  id="phoneNumber"
                  name="phoneNumber"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="Enter your phone number"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="company">Company</Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Enter your company name"
                />
              </FormGroup>
            </Section>

            <Section>
              <SectionHeader>
                <SectionTitle>Integrations</SectionTitle>
                <SectionDescription>Connect your favorite tools and services</SectionDescription>
              </SectionHeader>

              <IntegrationGrid>
                {integrations.map(integration => (
                  <IntegrationCard key={integration.id}>
                    <IntegrationHeader>
                      <IntegrationIcon>{integration.icon}</IntegrationIcon>
                      <IntegrationInfo>
                        <IntegrationTitle>{integration.name}</IntegrationTitle>
                        <IntegrationStatus connected={integration.connected}>
                          {integration.connected ? 'Connected' : 'Not Connected'}
                        </IntegrationStatus>
                      </IntegrationInfo>
                    </IntegrationHeader>

                    <IntegrationDescription>
                      {integration.description}
                    </IntegrationDescription>

                    <div className="api-key-content">
                      {integration.isEditing ? (
                        <Input
                          type="password"
                          value={integration.key}
                          onChange={(e) => handleIntegrationChange(integration.id, e.target.value)}
                          placeholder={`Enter your ${integration.name} API Key`}
                        />
                      ) : (
                          <ApiKeyDisplay>
                            {integration.key ? '•'.repeat(20) : 'No API key set'}
                          </ApiKeyDisplay>
                        )}
                    </div>

                    <ButtonContainer>
                      {integration.isEditing ? (
                        <>
                          <ActionButton
                            variant="primary"
                            onClick={() => handleIntegrationSave(integration.id)}
                          >
                            Save
                          </ActionButton>
                          <ActionButton
                            variant="danger"
                            onClick={() => handleIntegrationEdit(integration.id)}
                          >
                            Cancel
                          </ActionButton>
                        </>
                      ) : (
                        <ActionButton
                          onClick={() => handleIntegrationEdit(integration.id)}
                        >
                          {integration.connected ? 'Edit' : 'Connect'}
                        </ActionButton>
                      )}
                    </ButtonContainer>
                  </IntegrationCard>
                ))}
              </IntegrationGrid>
            </Section>

            <ButtonContainer>
              <SaveButton type="submit">
                Save Changes
              </SaveButton>
            </ButtonContainer>
          </form>
        </SettingsContainer>
      </MainSection>
    </DashboardContainer>
  );
};

export default Settings; 